import React from 'react';
import { X, Building2, Mail, Phone, Clock, Users2, ChevronDown, ChevronRight, Tag } from 'lucide-react';
import { useState, useEffect } from 'react';
import type { Summary } from '../pages/Summaries';
import { useFollowUps } from '../context/FollowUpsContext';

interface ContactDetailsModalProps {
  contact: {
    id: string;
    first_name: string;
    last_name: string;
    company: string;
    role: string;
    email: string;
    phone: string;
    created_at: string;
  };
  onClose: () => void;
}

const ContactDetailsModal = ({ contact, onClose }: ContactDetailsModalProps) => {
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllSummaries, setShowAllSummaries] = React.useState(false);
  const [showFollowUps, setShowFollowUps] = React.useState(false);
  const { followUps } = useFollowUps();

  useEffect(() => {
    const fetchSummaries = async () => {
      try {
        const { data, error } = await supabase
          .from('summaries')
          .select(`
            *,
            double:double_id (name),
            follow_ups (*)
          `)
          .eq('contact_id', contact.id)
          .order('date', { ascending: false });

        if (error) throw error;
        setSummaries(data || []);
      } catch (err) {
        console.error('Error fetching summaries:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSummaries();
  }, [contact.id]);

  // Get the last interaction
  const lastInteraction = summaries[0];

  // Get all follow-ups for this contact
  const contactFollowUps = summaries.flatMap(summary => 
    summary.followUps.map(followUp => ({
      ...followUp,
      summaryTitle: summary.title,
      summaryDate: summary.date,
      double: summary.double.name
    }))
  );

  // Group follow-ups by tag
  const groupedFollowUps = contactFollowUps.reduce((acc, followUp) => {
    const tag = followUps[followUp.id]?.tag || 'triage';
    if (!acc[tag]) acc[tag] = [];
    acc[tag].push(followUp);
    return acc;
  }, {} as Record<string, typeof contactFollowUps>);

  const tagConfig = {
    triage: { label: 'Triage', color: 'bg-blue-500' },
    own: { label: 'Own', color: 'bg-green-500' },
    delegate: { label: 'Delegate', color: 'bg-yellow-500' },
    archive: { label: 'Archive', color: 'bg-red-500' }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div 
        className="bg-white w-full max-w-4xl my-8 rounded-xl shadow-retro border-2 border-dark"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-primary font-semibold text-lg">
                {contact.first_name[0]}{contact.last_name[0]}
              </span>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{contact.first_name} {contact.last_name}</h2>
              <p className="text-gray-600">{contact.role} at {contact.company}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Contact Info */}
        <div className="p-6 bg-gray-50 border-b border-gray-200">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="flex items-center gap-2 text-gray-600">
              <Mail className="h-5 w-5" />
              <span>{contact.email}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Phone className="h-5 w-5" />
              <span>{contact.phone}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Building2 className="h-5 w-5" />
              <span>{contact.company}</span>
            </div>
          </div>
        </div>

        {/* Last Interaction */}
        {lastInteraction && (
          <div className="p-6 border-b border-gray-200">
            <div className="mb-4">
              <h3 className="text-sm font-medium text-gray-500 mb-3">Last Interaction</h3>
              <div className="flex items-center gap-4 text-sm">
                <Clock className="h-4 w-4 text-primary" />
                <span>{lastInteraction.date} at {lastInteraction.time}</span>
                <span>•</span>
                <span>with {lastInteraction.double}</span>
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-2">{lastInteraction.title}</h4>
              <p className="text-gray-600 text-sm">{lastInteraction.summary}</p>
            </div>
          </div>
        )}

        {/* Interaction History */}
        {summaries.length > 1 && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Previous Interactions</h3>
              {!showAllSummaries && summaries.length > 2 && (
                <button
                  onClick={() => setShowAllSummaries(true)}
                  className="text-primary hover:text-primary/80 text-sm font-medium"
                >
                  Show All ({summaries.length - 1})
                </button>
              )}
            </div>
            <div className="space-y-4">
              {(showAllSummaries ? summaries.slice(1) : summaries.slice(1, 2)).map((summary) => (
                <div key={summary.id} className="w-full text-left bg-white p-4 rounded-lg border-2 border-dark">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-900">{summary.title}</h4>
                      <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          <span>{summary.date} at {summary.time}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Users2 className="h-4 w-4" />
                          <span>with {summary.double}</span>
                        </div>
                      </div>
                      <p className="mt-2 text-gray-600 line-clamp-2">{summary.summary}</p>
                      <p className="mt-2 text-gray-600">{summary.summary}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Follow-ups Section */}
        {contactFollowUps.length > 0 && (
          <div className="p-6 border-t border-gray-200">
            <button
              onClick={() => setShowFollowUps(!showFollowUps)}
              className="w-full flex items-center justify-between text-left mb-4 group"
            >
              <h3 className="text-lg font-semibold">Follow-ups</h3>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">{contactFollowUps.length} items</span>
                <ChevronRight 
                  className={`h-5 w-5 text-gray-400 transition-transform duration-300 ${
                    showFollowUps ? 'rotate-90' : ''
                  }`}
                />
              </div>
            </button>
            
            <div className={showFollowUps ? 'follow-ups-enter' : 'follow-ups-exit'}>
              {showFollowUps && <div className="space-y-6">
                {Object.entries(tagConfig).map(([tag, config]) => {
                  const items = groupedFollowUps[tag] || [];
                  return items.length > 0 && (
                    <div key={tag}>
                      <div className={`flex items-center gap-2 px-3 py-1.5 ${config.color} text-white rounded-lg mb-3`}>
                        <Tag className="h-4 w-4" />
                        <span className="font-medium">{config.label}</span>
                        <span className="ml-auto bg-white/20 px-2 py-0.5 rounded-full text-sm">
                          {items.length}
                        </span>
                      </div>
                      <div className="space-y-3">
                        {items.map((followUp) => (
                          <div
                            key={followUp.id}
                            className="bg-white p-4 rounded-lg border border-gray-200"
                          >
                            <h4 className="font-medium text-gray-900 mb-2">{followUp.title}</h4>
                            <p className="text-sm text-gray-600 mb-3">{followUp.description}</p>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <div className="flex items-center gap-1">
                                <Clock className="h-4 w-4" />
                                <span>{followUp.summaryDate}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Users2 className="h-4 w-4" />
                                <span>with {followUp.double}</span>
                              </div>
                              <a 
                                href={`/summaries?id=${followUp.id}`}
                                className="text-primary hover:text-primary/80"
                              >
                                {followUp.summaryTitle}
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactDetailsModal;